import { type Signal, useSignal } from "@preact/signals";
import type { ComponentChildren, GenericEventHandler, RefObject } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "sonner";
import { type FileEntry, type ShareViewCommand, thumbnailName } from "~/protocol";
import { billingOn } from "~/web/src/billing-on";
import type { Channel } from "~/web/src/channel";
import { bytes } from "~/web/src/format";
import { TextTab } from "~/web/src/project/text-tab";
import { CREDITS_TITLE, creditCount, unlockRefusal } from "~/web/src/project/unlock-copy";
import { navigate } from "~/web/src/router";
import { setClipboard } from "../components/clipboard";
import { CopyIcon, DownloadIcon, ShareIcon, UnlockIcon } from "../components/icons";

// The pane above the feed (mockup ux/06). It follows the agent: the explorer's
// live browser while one is open, else the newest rendered mp4 (paused, with
// controls — no autoplay; its thumbnail, `[name].jpg` in the same listing, is
// the poster), else nothing. A file the user picks in the Files sheet (or the director
// pushes with open_tab) takes the pane over until the agent's next change — a
// new live browser or a newer video — hands it back. Client-local, like the
// old tabs.
//
// Every state wears the same bar on top (title · detail · actions · ✕). The
// pane is a stack — the user's pick over the agent's view (live browser, else
// newest video) over the collapsed line — and ✕ peels one layer: closing a
// pick shows the agent's view, closing the agent's view collapses the pane to
// its one line. Clicking the line, or any of the agent's changes above,
// expands it again. The browser bar can also go wide — the pane fills the
// window under the topbar, for typing into the live site directly.

/** The newest entry matching `keep`, by mtime. */
export function newest(files: FileEntry[], keep: (f: FileEntry) => boolean): FileEntry | null {
  let best: FileEntry | null = null;
  for (const f of files) {
    if (keep(f) && (!best || f.mtime > best.mtime)) best = f;
  }
  return best;
}

// The explorer's session recording is an mp4 too; the rendered video is what the pane is for.
export const isRendered = (f: FileEntry): boolean =>
  f.kind === "video" && f.name.endsWith(".mp4") && !f.name.startsWith("recording-");

/** The unlock, sent from the pane or the Files sheet: the feed shows it as a card, the
 * entry's `watermark` flips on the `file` event and the balance follows the channel's
 * `credits`, so only a refusal needs a word here. */
export async function unlockVideo(channel: Channel, fileName: string): Promise<void> {
  const refusal = unlockRefusal(await channel.send({ kind: "unlock_video", fileName }));
  if (!refusal) return;
  toast.error(refusal.text, {
    // One at a time: a repeat click replaces the toast rather than stacking another.
    id: "unlock-refusal",
    // An offer to act stays until it is taken or closed; an instruction stays long enough to
    // read, not the default 4 s.
    ...(refusal.action
      ? {
          action: { label: refusal.action, onClick: () => navigate("/billing") },
          duration: Number.POSITIVE_INFINITY,
          closeButton: true,
        }
      : { duration: 10_000 }),
  });
}

/** A watermarked render's "Remove watermark", with the org's balance beside it; nothing with
 * billing off or on a clean video. */
function Unlock({ channel, entry }: { channel: Channel; entry: FileEntry }) {
  const [pending, setPending] = useState(false);
  if (!billingOn || entry.watermark !== true) return null;
  const credits = channel.credits.value;
  return (
    <span class="unlock-group">
      <button
        type="button"
        class="btn ghost unlock"
        // Not `disabled`: that drops focus to the page, and the refusal's action is then
        // eleven Tabs away for a keyboard user.
        aria-disabled={pending}
        onClick={async () => {
          if (pending) return;
          setPending(true);
          await unlockVideo(channel, entry.name);
          setPending(false);
        }}
      >
        <UnlockIcon size={16} />
        Remove watermark
      </button>
      {credits !== null && (
        <span class="credits dim" title={CREDITS_TITLE}>
          {creditCount(credits)}
        </span>
      )}
    </span>
  );
}

function Download({ href, name }: { href: string; name: string }) {
  return (
    // On the collapsed line the bar itself is the expand button; a download is not a click on it.
    <a class="btn ghost" href={href} download={name} onClick={(e) => e.stopPropagation()}>
      <DownloadIcon />
      Download
    </a>
  );
}

function BottomBar({
  channel,
  entry,
  collapsed,
  closeLabel,
  onClose,
  onExpand,
}: {
  channel: Channel;
  entry: FileEntry;
  collapsed: boolean;
  closeLabel: string;
  onClose: () => void;
  onExpand: () => void;
}) {
  const [shareLink, setShareLink] = useState(entry.share?.link);
  const [expandedCopy, setExpandedCopy] = useState(false);
  const iframeText = `<iframe 
  src="${shareLink}" 
  width="800" 
  height="500" 
  title="Featurette"
  style="border: none;">
</iframe>`;
  return (
    <Bar collapsed={collapsed} closeLabel={closeLabel} onClose={onClose} onExpand={onExpand}>
      {shareLink ? (
        <div class="bottom-bar">
          <div class="bottom-bar-row">
            <button
              type="button"
              class="btn ghost"
              onClick={async () => {
                setExpandedCopy(!expandedCopy);
              }}
            >
              Copy Options
            </button>
            <Unlock channel={channel} entry={entry} />
          </div>
          {expandedCopy && (
            <div>
              <div class="copy-btn-row">
                <button
                  type="button"
                  class="btn ghost"
                  onClick={async () => {
                    await setClipboard(shareLink);
                    toast.success("Copied", {
                      duration: 1500,
                    });
                  }}
                >
                  <CopyIcon /> Direct Link
                </button>
                <span class="url">{shareLink}</span>
              </div>
              <div class="copy-btn-row">
                <button
                  type="button"
                  class="btn ghost"
                  onClick={async () => {
                    await setClipboard(iframeText);
                    toast.success("Copied", {
                      duration: 1500,
                    });
                  }}
                >
                  <CopyIcon /> Embed Link
                </button>
                <div class="bottom-bar-iframe-div">
                  <textarea rows={7} class="bottom-bar-iframe-text">
                    {iframeText}
                  </textarea>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div class="bottom-bar-row">
          <button
            type="button"
            class="btn ghost"
            onClick={async () => {
              const newShareResult = await channel.send({
                kind: "share_project_file",
                fileName: entry.name,
              });
              if (newShareResult.payload?.kind === "share_project_file") {
                setShareLink(newShareResult.payload.share.videoLink);
                setExpandedCopy(true);
              }
            }}
          >
            <ShareIcon />
            Share
          </button>
          <Unlock channel={channel} entry={entry} />
        </div>
      )}
    </Bar>
  );
}

function FileBody({ channel, entry }: { channel: Channel; entry: FileEntry }) {
  const src = channel.fileUrl(entry.name, entry.version);
  if (entry.pending) return <div class="idle-note">{entry.name} is being generated…</div>;
  switch (entry.kind) {
    case "text":
      return <TextTab channel={channel} name={entry.name} />;
    case "video": {
      // TODO: thumbnails for shares; shares do not have files other than their single uploaded video file;
      //       ; embed required logic into VideoLinkBody
      // A render's thumbnail sits beside it; recordings and uploads have none.
      const thumb = channel.files.value.find((f) => f.name === thumbnailName(entry.name));
      const poster = thumb ? channel.fileUrl(thumb.name, thumb.version) : undefined;
      // biome-ignore lint/a11y/useMediaCaption: renders burn their captions in, recordings carry none
      return <video key={entry.name} src={src} poster={poster} controls preload="metadata" />;
    }
    case "audio":
      return (
        <div class="audio-card">
          {/* biome-ignore lint/a11y/useMediaCaption: music beds carry no captions */}
          <audio src={src} controls preload="metadata" />
          <div class="dim">
            {entry.name} · {bytes(entry.size)}
          </div>
        </div>
      );
    case "image":
      return <img src={src} alt={entry.name} />;
    default:
      return (
        <div class="idle-note">
          <Download href={src} name={entry.name} />
          <div class="dim" style="margin-top:8px">
            {entry.kind} · {bytes(entry.size)}
          </div>
        </div>
      );
  }
}

/** The pane's one bar. Expanded: ✕ closes one layer. Collapsed: the whole line expands. */
function Bar({
  collapsed,
  closeLabel,
  includeCloseButton,
  onClose,
  onExpand,
  children,
}: {
  collapsed: boolean;
  closeLabel: string;
  onClose: () => void;
  onExpand: () => void;
  includeCloseButton?: boolean;
  children: ComponentChildren;
}) {
  if (collapsed) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: the line carries a Download link, which a <button> cannot nest
      <div
        class="bar closed"
        role="button"
        tabIndex={0}
        aria-label="Expand"
        onClick={onExpand}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onExpand();
          }
        }}
      >
        {children}
        <span class="x" aria-hidden="true">
          ▾
        </span>
      </div>
    );
  }
  return (
    <div class="bar">
      {children}
      {includeCloseButton && (
        <button
          type="button"
          class="x"
          aria-label={closeLabel}
          title={closeLabel}
          onClick={onClose}
        >
          ✕
        </button>
      )}
    </div>
  );
}

function WideToggle({ wide, onToggle }: { wide: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      class="x"
      aria-label={wide ? "Back to normal size" : "Fill the window"}
      title={wide ? "Back to normal size" : "Fill the window"}
      onClick={onToggle}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        {wide ? (
          <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
        ) : (
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
        )}
      </svg>
    </button>
  );
}

export function TopPane({
  channel,
  viewing,
}: {
  channel: Channel;
  viewing: Signal<string | null>;
}) {
  const files = channel.files.value;
  const browser = channel.browser.value;
  const video = newest(files, isRendered);
  const picked = viewing.value ? files.find((f) => f.name === viewing.value) : undefined;
  const collapsed = useSignal(false);
  const wide = useSignal(false);

  // The agent's changes hand the pane back and open it: a new live browser, a newer video.
  useEffect(
    () =>
      channel.onBrowserOpen(() => {
        viewing.value = null;
        collapsed.value = false;
      }),
    [channel, viewing, collapsed],
  );
  const seen = useRef(video?.name);
  useEffect(() => {
    if (video?.name !== seen.current) {
      seen.current = video?.name;
      if (video) {
        viewing.value = null;
        collapsed.value = false;
      }
    }
  }, [video, viewing, collapsed]);
  // A pick (Files sheet, open_tab) opens it too.
  const pickedName = viewing.value;
  useEffect(() => {
    if (pickedName) collapsed.value = false;
  }, [pickedName, collapsed]);
  // Wide belongs to the live browser; it goes when the session does.
  useEffect(() => {
    if (!browser) wide.value = false;
  }, [browser, wide]);

  // ✕ peels one layer: a pick closes to the agent's view, the agent's view collapses.
  const bar = {
    collapsed: collapsed.value,
    closeLabel: picked ? "Close" : "Collapse",
    onClose: picked
      ? () => {
          viewing.value = null;
        }
      : () => {
          collapsed.value = true;
          wide.value = false;
        },
    onExpand: () => {
      collapsed.value = false;
    },
  };

  // Precedence: the user's pick, the live browser, the newest video.
  const entry = picked ?? (browser ? null : video);
  if (entry) {
    return (
      <div class="pane">
        <Bar {...bar} includeCloseButton>
          <span class="url">{entry.name}</span>
          <span class="dim">{entry.pending ? "generating" : bytes(entry.size)}</span>
          <Download href={channel.fileUrl(entry.name, entry.version)} name={entry.name} />
        </Bar>
        {!collapsed.value && (
          <>
            <div class={`content ${entry.kind}`}>
              <FileBody channel={channel} entry={entry} />
            </div>
            {entry.kind === "video" && (
              <BottomBar
                key={`${entry.name}:${entry.version}`}
                channel={channel}
                entry={entry}
                collapsed={bar.collapsed}
                closeLabel={bar.closeLabel}
                onClose={bar.onClose}
                onExpand={bar.onExpand}
              />
            )}
          </>
        )}
      </div>
    );
  }
  if (browser) {
    return (
      <div class={wide.value ? "pane wide" : "pane"}>
        <Bar {...bar} includeCloseButton>
          <span class="dots">
            <i />
            <i />
            <i />
          </span>
          <span class="url">{browser.liveUrl ?? channel.project.value?.targetUrl ?? ""}</span>
          <span class="live">
            <i />
            live
          </span>
          {browser.liveUrl && !collapsed.value && (
            <WideToggle
              wide={wide.value}
              onToggle={() => {
                wide.value = !wide.value;
              }}
            />
          )}
        </Bar>
        {!collapsed.value && (
          <div class="content site">
            {browser.liveUrl ? (
              <iframe
                src={browser.liveUrl}
                title="live browser"
                allow="clipboard-read; clipboard-write"
              />
            ) : (
              <div class="idle-note">Browser session open — no live view was provided.</div>
            )}
          </div>
        )}
      </div>
    );
  }
  return null;
}

export function ShareVideoPanel({
  name,
  link,
  socket,
}: {
  name: string;
  link: string;
  socket: WebSocket;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bar = {
    collapsed: false,
    closeLabel: "Collapse",
    onClose: () => {},
    onExpand: () => {},
  };
  const send = (cmd: ShareViewCommand) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(cmd));
    }
  };

  useEffect(() => {
    let timeoutId: number;
    const ping = () => {
      const video = videoRef.current;
      if (video) {
        const current = video.currentTime;
        send({
          kind: "ping",
          playbackPositionSeconds: current,
        });
      }
      timeoutId = setTimeout(ping, 5000);
    };
    ping();
    return () => clearTimeout(timeoutId);
  }, []);

  const [playSent, setPlaySent] = useState(false);
  const onPlay = () => {
    if (!playSent) {
      send({
        kind: "play",
      });
      setPlaySent(true);
    }
  };

  const inIframe = window.self !== window.top; // https://stackoverflow.com/a/326076/5353940
  return (
    <div class={`pane${inIframe ? ` in-iframe` : ""}`}>
      <Bar {...bar}>
        <span class="url">{name}</span>
        <Download href={link} name={name} />
      </Bar>
      <div class={`content video${inIframe ? ` in-iframe` : ``}`}>
        <VideoLinkBody videoRef={videoRef} name={name} link={link} onPlay={onPlay} />
      </div>
    </div>
  );
}

function VideoLinkBody({
  name,
  link,
  videoRef,
  onPlay,
}: {
  name: string;
  link: string;
  videoRef: RefObject<HTMLVideoElement>;
  onPlay: GenericEventHandler<HTMLVideoElement>;
}) {
  // biome-ignore lint/a11y/useMediaCaption: renders burn their captions in, recordings carry none
  return <video key={name} src={link} controls preload="metadata" ref={videoRef} onPlay={onPlay} />;
}
